import Link from 'next/link'

import type { FC, ReactNode } from 'react'

import { ArrowRight } from 'lucide-react'

import { Container } from '@/components/generals/container'
import { buttonClass } from '@/components/generals/button'
import { PillarChip } from '@/components/content/pillar-chip'
import { HUB_NAV, hubPresentation } from '@/constants/hubs'

export const HomeHero: FC = (): ReactNode => {
    return (
        <section className='relative overflow-hidden'>
            <Container className='grid items-center gap-12 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:py-24'>
                <div className='flex flex-col items-start gap-6'>
                    <span className='inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground'>
                        <span className='size-1.5 rounded-full bg-primary' />
                        Whole-person wellness, made practical
                    </span>

                    <h1 className='font-heading text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl lg:text-6xl'>
                        Wellness that treats you as one connected system.
                    </h1>

                    <p className='max-w-xl text-lg leading-relaxed text-muted-foreground'>
                        WellPath Fusion is an evidence-aware guide to feeling better in body and
                        mind. We cut through the noise with clear, science-grounded writing across
                        ten in-depth guides to everyday health.
                    </p>

                    <div className='flex flex-wrap items-center gap-3'>
                        <Link href='#topics' className={buttonClass({ size: 'lg' })}>
                            Explore the guides
                            <ArrowRight className='size-4' />
                        </Link>
                        <Link href='#journal' className={buttonClass({ variant: 'outline', size: 'lg' })}>
                            Read the latest
                        </Link>
                    </div>

                    <div className='flex flex-wrap gap-2 pt-2'>
                        {HUB_NAV.map((group) => (
                            <PillarChip
                                key={group.slug}
                                accent={hubPresentation(group.slug).accent}
                                label={group.label}
                                href={`/${group.slug}`}
                            />
                        ))}
                    </div>
                </div>

                <div className='relative'>
                    <div className='relative aspect-[5/6] overflow-hidden rounded-3xl border border-border bg-muted sm:aspect-[4/3] lg:aspect-[5/6]'>
                        <img
                            src='/images/hero.jpg'
                            alt='A calm moment of mindful movement in soft natural light'
                            className='absolute inset-0 size-full object-cover'
                        />
                    </div>
                </div>
            </Container>
        </section>
    )
}
